import { HTTPError } from 'ky';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';

import { Alert, AlertType, CrowdingAlert, EarningsAlert, SentimentAlert } from '../model/alert';
import { AnalystSeniority, RawAnalystSeniority } from '../model/analystSeniority';
import { AppPage, UserAction, UtmUserTrackingInput } from '../model/analytics';
import { BuysidePortfolioAttribution, BuysidePortfolioTimeFrames } from '../model/buysideportfolio';
import {
  BuysideIdeaReturnDataMap,
  Company,
  CompanyBorrowCost,
  CompanyDetails,
  CompanyEnfusionPriceDetails,
  CompanyOverviewMap,
  CompanyPriceTargetInput,
  CompanyStateChangeMap,
  CompanyStateComplete,
  UpdateCompanyStateInput,
} from '../model/company';
import { AmendIdeaInput, CloseIdeaInput, Idea, OpenIdeaInput } from '../model/idea';
import { LeagueSnapshot, LeagueTimeHorizon } from '../model/league';
import { NotificationActionInput } from '../model/notification';
import { Portfolio, PortfolioEnfusionPriceDetails, PortfolioPerformance } from '../model/portfolio';
import {
  AmendPositionInput,
  ClosedPosition,
  OpenPositionInput,
  Position,
  PositionLeg,
  RemovePositionInput,
} from '../model/position';
import { PriceTargetCalculations, RawPriceTargetCalculations } from '../model/priceTargetCalculations';
import { Sector } from '../model/sector';
import {
  AnalystTheme,
  AnalystThemeDetails,
  AnalystThemeInput,
  AnalystThemeLeg,
  AnalystThemeStatus,
  ArchiveThemeJSON,
  ParentTheme,
  ParentThemeDetails,
  ParentThemeInput,
  ParentThemeLeg,
  PendingCreationAnalystTheme,
  ThemeChangeRationale,
  ThemeCompanyActiveChangeTransition,
  ThemeCompanyState,
  ThemeCompanyStateTransition,
  ThemePendingChangeSubmissionDetails,
} from '../model/theme';
import { DirectionalThemePerformance, ThemeCompanyPerformance, ThemePerformance } from '../model/themePerformance';
import { RawTradingAnalytics, TradingAnalytics } from '../model/tradingAnalytics';
import { User } from '../model/user';
import { isNotNullish } from '../util/array';
import httpClient from './http';
import { LegalEventType } from './legal-event';
import { parseDateTime, parseDuration, RawDateTimes } from './parse';
import { mapAlertWithExpiration } from './queries/mappers/alerts.mapper';
import { fromServerMap as priceTargetsFromServerMap } from './queries/mappers/priceTargets.mapper';
import { fromServerMap as tradingAnalyticsFromServerMap } from './queries/mappers/tradingActivity.mapper';

// WORKAROUND for react-query v3 not supporting react types v18
declare module 'react-query/types/react/QueryClientProvider' {
  interface QueryClientProviderProps {
    children?: ReactNode;
  }
}

const Api = {
  addToCoverage: async (companyId: string) => {
    return httpClient.post('/api/coverage', {
      json: {
        companyId,
      },
    });
  },

  getCompanyOverview: async (): Promise<CompanyOverviewMap> => {
    const response: RawDateTimes<CompanyOverviewMap> = await httpClient.get(`/api/companies/overview`).json();

    return _.mapValues(response, (r) => {
      return {
        ...r,
        priceTarget:
          r.priceTarget === null
            ? null
            : {
                ...r.priceTarget,
                time: parseDateTime(r.priceTarget.time),
                duration: r.priceTarget.duration ? parseDuration(r.priceTarget.duration) : null,
                targetDate: r.priceTarget.targetDate ? parseDateTime(r.priceTarget.targetDate) : null,
                bearTargetDate: r.priceTarget.bearTargetDate ? parseDateTime(r.priceTarget.bearTargetDate) : null,
                bullTargetDate: r.priceTarget.bullTargetDate ? parseDateTime(r.priceTarget.bullTargetDate) : null,
              },
      };
    });
  },

  getAllIdeaReturnDataForUser: async (): Promise<BuysideIdeaReturnDataMap> => {
    const response: RawDateTimes<BuysideIdeaReturnDataMap> = await httpClient.get(`/api/idea-returns`).json();
    const result: BuysideIdeaReturnDataMap = {};
    Object.keys(response).forEach((ideaId) => {
      const data = response[ideaId];
      result[ideaId] = {
        ...data,
        ideaOpenTime: parseDateTime(data.ideaOpenTime),
      };
    });
    return result;
  },

  getCompanyDetails: async (companyId: string): Promise<CompanyDetails> => {
    const response: RawDateTimes<CompanyDetails> = await httpClient.get(`/api/companies/${companyId}/details`).json();
    return {
      ...response,
      stateHistory: _.map(response.stateHistory, (t) => ({
        ...t,
        transitionTime: parseDateTime(t.transitionTime),
      })),
      ideaHistories: _.map(response.ideaHistories, (h) => ({
        ...h,
        actions: _.map(h.actions, (a) => ({
          ...a,
          time: parseDateTime(a.time),
        })),
      })),
      positionHistories: _.map(response.positionHistories, (h) => ({
        ...h,
        actions: _.map(h.actions, (a) => ({
          ...a,
          time: parseDateTime(a.time),
        })),
      })),
      latestPriceTarget:
        response.latestPriceTarget === null
          ? null
          : {
              ...response.latestPriceTarget,
              time: parseDateTime(response.latestPriceTarget.time),
              duration: response.latestPriceTarget.duration ? parseDuration(response.latestPriceTarget.duration) : null,
              targetDate: response.latestPriceTarget.targetDate
                ? parseDateTime(response.latestPriceTarget.targetDate)
                : null,
              bearTargetDate: response.latestPriceTarget.bearTargetDate
                ? parseDateTime(response.latestPriceTarget.bearTargetDate)
                : null,
              bullTargetDate: response.latestPriceTarget.bullTargetDate
                ? parseDateTime(response.latestPriceTarget.bullTargetDate)
                : null,
            },
      priceTargetHistory: _.map(response.priceTargetHistory, (p) =>
        p.baseCase === null
          ? {
              ...p,
              time: parseDateTime(p.time),
              bearTargetDate: p.bearTargetDate ? parseDateTime(p.bearTargetDate) : null,
              bullTargetDate: p.bullTargetDate ? parseDateTime(p.bullTargetDate) : null,
            }
          : {
              ...p,
              time: parseDateTime(p.time),
              duration: p.duration ? parseDuration(p.duration) : null,
              targetDate: p.targetDate ? parseDateTime(p.targetDate) : null,
              bearTargetDate: p.bearTargetDate ? parseDateTime(p.bearTargetDate) : null,
              bullTargetDate: p.bullTargetDate ? parseDateTime(p.bullTargetDate) : null,
            },
      ),
    };
  },

  getCompanyData: async (companyId: string): Promise<Company> => {
    const response: RawDateTimes<CompanyDetails> = await httpClient.get(`/api/companies/${companyId}/details`).json();

    return response.company;
  },

  getCompanyStates: async (): Promise<CompanyStateComplete[]> => {
    return httpClient.get('/api/companyStates').json();
  },

  updateCompanyState: async (companyId: string, input: UpdateCompanyStateInput): Promise<CompanyStateChangeMap> => {
    return httpClient
      .put(`/api/companies/${companyId}/state`, {
        json: input,
      })
      .json();
  },

  applyCompanyStateChanges: async (stateChanges: CompanyStateChangeMap) => {
    return httpClient.put('/api/companies/state-changes', {
      json: stateChanges,
    });
  },

  cancelCompanyStateChanges: async (stateChanges: CompanyStateChangeMap) => {
    return httpClient.put('/api/companies/cancel-state-changes', {
      json: stateChanges,
    });
  },

  setCompanyPriceTarget: async (companyId: string, priceTarget: CompanyPriceTargetInput) => {
    return httpClient.put(`/api/companies/${companyId}/price-target`, {
      json: priceTarget,
    });
  },

  deleteCompanyPriceTarget: async (companyId: string) => {
    return httpClient.delete(`/api/companies/${companyId}/price-target`);
  },

  searchForCompany: async (query: string): Promise<Company[]> => {
    return httpClient
      .get(`/api/companies/search`, {
        searchParams: {
          query,
        },
      })
      .json();
  },

  getOpenIdeas: async (): Promise<Idea[]> => {
    const ideas = await httpClient.get('/api/ideas').json<RawDateTimes<Idea>[]>();
    return _(ideas)
      .map((idea) => ({
        ...idea,
        time: parseDateTime(idea.time),
      }))
      .value();
  },

  openIdea: async (input: OpenIdeaInput): Promise<string> => {
    return httpClient.post('/api/ideas', { json: input }).json();
  },

  amendIdea: async (ideaId: string, input: AmendIdeaInput): Promise<string> => {
    return httpClient.put(`/api/ideas/${ideaId}`, { json: input }).json();
  },

  closeIdea: async (ideaId: string, input: CloseIdeaInput): Promise<string> => {
    return httpClient.delete(`/api/ideas/${ideaId}`, { json: input }).json();
  },

  getParentThemes: async (): Promise<ParentTheme[]> => {
    const parentThemes = await httpClient.get('/api/themes/parent-themes').json<RawDateTimes<ParentTheme>[]>();
    return _(parentThemes)
      .map((parentTheme) => ({
        ...parentTheme,
        time: parseDateTime(parentTheme.time),
      }))
      .value();
  },

  getParentThemeLegs: async (): Promise<ParentThemeLeg[]> => {
    const parentThemeLegs = await httpClient
      .get('/api/themes/parent-theme-legs')
      .json<RawDateTimes<ParentThemeLeg>[]>();
    return _(parentThemeLegs)
      .map((parentThemeLeg) => ({
        ...parentThemeLeg,
        time: parseDateTime(parentThemeLeg.time),
      }))
      .value();
  },

  createParentTheme: async (input: ParentThemeInput): Promise<string> =>
    httpClient.post('/api/themes/parent-themes', { json: input }).json(),

  updateParentTheme: async (parentThemeId: string, input: ParentThemeInput): Promise<string> =>
    httpClient.put(`/api/themes/parent-themes/${parentThemeId}`, { json: input }).json(),

  createAnalystTheme: async (input: AnalystThemeInput): Promise<string> =>
    httpClient.post('/api/themes/analyst-themes', { json: input }).json(),

  updateAnalystTheme: async (analystThemeId: string, input: AnalystThemeInput): Promise<string> =>
    httpClient.put(`/api/themes/analyst-themes/${analystThemeId}`, { json: input }).json(),

  removeCompanyFromAnalystTheme: async (companyId: string, analystThemeId: string): Promise<Response> =>
    httpClient.delete(`/api/themes/analyst-themes/${analystThemeId}/companies/${companyId}`),

  getAnalystThemes: async (): Promise<AnalystTheme[]> => {
    const analystThemes = await httpClient.get('/api/themes/analyst-themes').json<RawDateTimes<AnalystTheme>[]>();
    return _(analystThemes)
      .map((analystTheme) => ({
        ...analystTheme,
        startDate: analystTheme.startDate !== null ? parseDateTime(analystTheme.startDate) : null,
        time: parseDateTime(analystTheme.time),
        createdTime: parseDateTime(analystTheme.createdTime),
      }))
      .value();
  },

  getPendingCreationThemes: async (): Promise<PendingCreationAnalystTheme[]> => {
    const pendingThemes = await httpClient
      .get('/api/themes/analyst-themes/pending-creation')
      .json<RawDateTimes<PendingCreationAnalystTheme>[]>();
    return _(pendingThemes)
      .map((analystTheme) => ({
        ...analystTheme,
        status: AnalystThemeStatus.NORMAL,
      }))
      .value();
  },

  getAnalystThemesForSector: async (sectorId: string | undefined): Promise<AnalystTheme[]> => {
    const analystThemes = await httpClient
      .get(`/api/themes/analyst-themes/sector/${sectorId ?? 'undefined'}`)
      .json<RawDateTimes<AnalystTheme>[]>();
    return _(analystThemes)
      .map((analystTheme) => ({
        ...analystTheme,
        startDate: analystTheme.startDate !== null ? parseDateTime(analystTheme.startDate) : null,
        time: parseDateTime(analystTheme.time),
        createdTime: parseDateTime(analystTheme.createdTime),
      }))
      .value();
  },

  getAnalystThemeLegs: async (): Promise<AnalystThemeLeg[]> => {
    const analystThemeLegs = await httpClient
      .get('/api/themes/analyst-theme-legs')
      .json<RawDateTimes<AnalystThemeLeg>[]>();
    return _(analystThemeLegs)
      .map((analystThemeLeg) => ({
        ...analystThemeLeg,
        startDate: analystThemeLeg.startDate !== null ? parseDateTime(analystThemeLeg.startDate) : null,
        time: parseDateTime(analystThemeLeg.time),
        approvalStatus: analystThemeLeg.approvalStatus,
      }))
      .value();
  },

  archiveTheme: async (analystThemeId: string, json: ArchiveThemeJSON) => {
    return httpClient.patch(`/api/themes/analyst-themes/${analystThemeId}`, { json });
  },

  deleteTheme: async (analystThemeId: string, { rationale }: ThemeChangeRationale) => {
    return httpClient.delete(`/api/themes/analyst-themes/${analystThemeId}`, {
      json: rationale ? { rationale: rationale } : undefined,
    });
  },

  changeThemeStatus: async (
    companyId: string,
    analystThemeId: string,
    active: boolean,
  ): Promise<ThemeCompanyActiveChangeTransition> => {
    const themeCompanyStateTransition = await httpClient
      .patch(`/api/themes/analyst-themes/${analystThemeId}/companies/${companyId}`, {
        json: { active },
      })
      .json<RawDateTimes<ThemeCompanyActiveChangeTransition>>();

    return {
      ...themeCompanyStateTransition,
      time: parseDateTime(themeCompanyStateTransition.time),
    };
  },

  getThemeCompanyStates: async (): Promise<ThemeCompanyState[]> => {
    const themeCompanyStates = await httpClient
      .get('/api/themes/theme-company-states')
      .json<RawDateTimes<ThemeCompanyState>[]>();
    return _(themeCompanyStates)
      .map((themeCompanyState) => ({
        ...themeCompanyState,
        time: parseDateTime(themeCompanyState.time),
        createdTime: parseDateTime(themeCompanyState.createdTime),
      }))
      .value();
  },

  getThemeCompanyStateTransitions: async (): Promise<ThemeCompanyStateTransition[]> => {
    const themeCompanyStateTransitions = await httpClient
      .get('/api/themes/theme-company-state-transitions')
      .json<RawDateTimes<ThemeCompanyStateTransition>[]>();
    return _(themeCompanyStateTransitions)
      .map((themeCompanyStateTransition) => ({
        ...themeCompanyStateTransition,
        time: parseDateTime(themeCompanyStateTransition.time),
      }))
      .value();
  },

  getThemeCompanies: async (): Promise<Company[]> => {
    return httpClient
      .extend({
        timeout: 10000,
      })
      .get('/api/themes/theme-companies')
      .json();
  },

  getParentThemeDetails: async (
    parentThemeId: string,
    includeThemePendingCreation?: boolean,
  ): Promise<ParentThemeDetails> => {
    const parentThemeDetails: RawDateTimes<ParentThemeDetails> = await httpClient
      .get(`/api/themes/parent-themes/${parentThemeId}/details`, {
        ...(includeThemePendingCreation && {
          searchParams: {
            includeThemePendingCreation: 'true',
          },
        }),
      })
      .json();

    return {
      ...parentThemeDetails,
      createdTime: parseDateTime(parentThemeDetails.createdTime),
      parentTheme: { ...parentThemeDetails.parentTheme, time: parseDateTime(parentThemeDetails.parentTheme.time) },
    };
  },

  getAnalystThemesForParentTheme: async (parentThemeId: string): Promise<AnalystTheme[]> => {
    const analystThemes: RawDateTimes<AnalystTheme>[] = await httpClient
      .get(`/api/themes/parent-themes/${parentThemeId}/analyst-themes`)
      .json();
    return _(analystThemes)
      .map((theme) => ({
        ...theme,
        startDate: theme.startDate !== null ? parseDateTime(theme.startDate) : null,
        time: parseDateTime(theme.time),
        createdTime: parseDateTime(theme.createdTime),
      }))
      .value();
  },

  getAnalystThemeDetails: async (
    analystThemeId: string | undefined,
    includeThemePendingCreation?: boolean,
  ): Promise<AnalystThemeDetails> => {
    const analystThemeDetails: RawDateTimes<AnalystThemeDetails> | HTTPError = await httpClient
      .extend({
        throwHttpErrors: false,
      })
      .get(`/api/themes/analyst-themes/${analystThemeId ?? 'undefined'}/details`, {
        ...(includeThemePendingCreation && {
          searchParams: {
            includeThemePendingCreation: 'true',
          },
        }),
      })
      .json();

    function isHTTPError(response: RawDateTimes<AnalystThemeDetails> | HTTPError): response is HTTPError {
      return !('analystTheme' in response);
    }

    if (isHTTPError(analystThemeDetails)) {
      if (analystThemeDetails.response.status === 404) {
        throw new Error('Theme not found');
      } else {
        throw new Error('Unexpected error');
      }
    }

    return {
      ...analystThemeDetails,
      analystTheme: {
        ...analystThemeDetails.analystTheme,
        startDate:
          analystThemeDetails.analystTheme.startDate !== null
            ? parseDateTime(analystThemeDetails.analystTheme.startDate)
            : null,
        time: parseDateTime(analystThemeDetails.analystTheme.time),
        createdTime: parseDateTime(analystThemeDetails.analystTheme.createdTime),
      },
    };
  },

  getCompaniesForAnalystTheme: async (analystThemeId: string | undefined): Promise<Company[]> => {
    return await httpClient.get(`/api/themes/analyst-themes/${analystThemeId ?? 'undefined'}/companies`).json();
  },

  searchForTheme: async (query: string): Promise<(AnalystTheme | ParentTheme)[]> => {
    return await httpClient
      .get(`/api/themes/search`, {
        searchParams: {
          query,
        },
      })
      .json();
  },

  isTosAccepted: async (): Promise<boolean> => {
    try {
      return await httpClient.get(`/api/event/legal/tos-acceptance`).json<boolean>();
    } catch (e) {
      return false;
    }
  },

  logLegalEvent: async (type: LegalEventType): Promise<Response> =>
    httpClient.post('/api/event/legal', {
      searchParams: {
        type,
      },
    }),

  getPositionByCompanyId: async (companyId: string): Promise<Position> => {
    const position = await httpClient.get(`api/positions/active/company/${companyId}`).json<RawDateTimes<Position>>();
    return {
      ...position,
      time: parseDateTime(position.time),
    };
  },

  getPositions: async (): Promise<Position[]> => {
    const positions = await httpClient.get('/api/positions').json<RawDateTimes<Position>[]>();
    return _(positions)
      .map((position) => ({
        ...position,
        time: parseDateTime(position.time),
      }))
      .value();
  },

  getPositionsInSector: async (sectorId: string | undefined): Promise<Position[]> => {
    const positions = await httpClient
      .get(`api/positions/active/sector/${sectorId ?? 'undefined'}`)
      .json<RawDateTimes<Position>[]>();
    return _(positions)
      .map((position) => ({
        ...position,
        time: parseDateTime(position.time),
      }))
      .value();
  },

  getPositionsLegs: async (): Promise<PositionLeg[]> => {
    const positions = await httpClient.get('/api/positions/position-legs').json<RawDateTimes<PositionLeg>[]>();
    return _(positions)
      .map((position) => ({
        ...position,
        time: parseDateTime(position.time),
      }))
      .value();
  },

  getClosedPositions: async (): Promise<ClosedPosition[]> => {
    const positions = await httpClient.get('/api/positions/removed').json<RawDateTimes<ClosedPosition>[]>();
    return _(positions)
      .map((position) => ({
        ...position,
        time: parseDateTime(position.time),
        priceTargetAtClose: position.priceTargetAtClose
          ? {
              ...position.priceTargetAtClose,
              time: parseDateTime(position.priceTargetAtClose.time),
              duration: position.priceTargetAtClose.duration
                ? parseDuration(position.priceTargetAtClose.duration)
                : null,
              targetDate: position.priceTargetAtClose.targetDate
                ? parseDateTime(position.priceTargetAtClose.targetDate)
                : null,
              bearTargetDate: position.priceTargetAtClose.bearTargetDate
                ? parseDateTime(position.priceTargetAtClose.bearTargetDate)
                : null,
              bullTargetDate: position.priceTargetAtClose.bullTargetDate
                ? parseDateTime(position.priceTargetAtClose.bullTargetDate)
                : null,
            }
          : null,
      }))
      .value();
  },

  openPosition: async (input: OpenPositionInput): Promise<string> => {
    return httpClient.post('/api/positions', { json: input }).json();
  },

  amendPosition: async (positionId: string, input: AmendPositionInput): Promise<string> => {
    return httpClient.put(`/api/positions/${positionId}`, { json: input }).json();
  },

  removePosition: async (positionId: string, input: RemovePositionInput): Promise<string> => {
    return httpClient.delete(`/api/positions/${positionId}`, { json: input }).json();
  },

  logSseConnectionFailure: async (): Promise<Response> => httpClient.post('/api/event/sse_failure'),

  getCompanyEnfusionPriceDetails: async (companyId: string): Promise<CompanyEnfusionPriceDetails> => {
    const priceDetails = await httpClient
      .get(`/api/companies/${companyId}/enfusionPriceDetails`)
      .json<RawDateTimes<CompanyEnfusionPriceDetails>>();
    return {
      ...priceDetails,
      priceTime: parseDateTime(priceDetails.priceTime),
    };
  },

  getPortfolioEnfusionPriceDetails: async (): Promise<PortfolioEnfusionPriceDetails> => {
    const portfolioPriceDetails = await httpClient
      .get(`/api/positions/enfusionPriceDetails`)
      .json<RawDateTimes<PortfolioEnfusionPriceDetails>>();
    return {
      ...portfolioPriceDetails,
      pullTime: parseDateTime(portfolioPriceDetails.pullTime),
      portfolioCompanyPricingDetails: _(portfolioPriceDetails.portfolioCompanyPricingDetails)
        .map((companyPriceDetails) => ({
          ...companyPriceDetails,
          priceTime: parseDateTime(companyPriceDetails.priceTime),
        }))
        .value(),
    };
  },

  getPortfolios: async (): Promise<Portfolio[]> => {
    return await httpClient.get('/api/portfolio').json<Portfolio[]>();
  },

  addToEnfusionCoverage: async (companyId: string): Promise<void> => {
    await httpClient.put(`/api/companies/${companyId}/addToEnfusionCoverage`);
  },

  getBuysidePortofioAttribution: async (
    timeFrame: BuysidePortfolioTimeFrames,
  ): Promise<BuysidePortfolioAttribution> => {
    const pfl = await httpClient
      .get(`/api/portfolio/attribution/fixed-time?timeHorizon=${timeFrame}`)
      .json<RawDateTimes<BuysidePortfolioAttribution>>();
    return {
      ...pfl,
      startDate: parseDateTime(pfl.startDate),
      endDate: parseDateTime(pfl.endDate),
      ideaAttributions: pfl.ideaAttributions.map((a) => ({
        ...a,
        closeTime: a.closeTime ? parseDateTime(a.closeTime) : null,
      })),
    };
  },

  getAlerts: async (type: AlertType, entityId?: string): Promise<Alert[]> => {
    const alerts = await httpClient
      .get('/api/alerts', {
        searchParams: {
          type,
          ...(entityId && { entityId }),
        },
      })
      .json<RawDateTimes<Alert[]>>();
    return _(alerts)
      .map((alert) => ({
        ...alert,
        beginTime: parseDateTime(alert.beginTime),
        endTime: (alert.endTime && parseDateTime(alert.endTime)) || null,
      }))
      .value();
  },

  getLeague: async (timeHorizon: LeagueTimeHorizon, forDate?: DateTime): Promise<LeagueSnapshot> => {
    const snapshot = await httpClient
      .get('api/league', {
        searchParams: {
          timeTimeHorizon: timeHorizon,
          ...(forDate && { forDate: forDate.toISODate() }),
        },
      })
      .json<RawDateTimes<LeagueSnapshot>>();
    return {
      ...snapshot,
      forDate: parseDateTime(snapshot.forDate),
      alphaBetaAsOfDate: parseDateTime(snapshot.alphaBetaAsOfDate),
      asOfDate: parseDateTime(snapshot.asOfDate),
    };
  },

  recordUserActionAnalyticsMetric: async (
    page: AppPage,
    action: UserAction,
    browserTime: DateTime,
    metaData: Record<string, string>,
  ): Promise<void> => {
    await httpClient.get(`/api/analytics/userAction/${page}/${action}`, {
      searchParams: {
        ...metaData,
        browserTime: browserTime.toISO(),
      },
    });
  },

  getImpersonationUsers: async (): Promise<User[]> => {
    return await httpClient.get('/api/impersonation/users').json();
  },

  getPriceTargets: async (): Promise<PriceTargetCalculations[]> => {
    const data = await httpClient
      .extend({
        timeout: 20000,
      })
      .get('/api/price-targets/calculations')
      .json<RawPriceTargetCalculations[]>();

    return priceTargetsFromServerMap(data);
  },
  getCompanyBorrowCost: async (companyId: string): Promise<CompanyBorrowCost> => {
    const borrowCost = await httpClient
      .get(`/api/companies/${companyId}/borrowCost`)
      .json<RawDateTimes<CompanyBorrowCost>>();
    return {
      ...borrowCost,
      rateDate: parseDateTime(borrowCost.rateDate),
    };
  },

  getPortfolioPerformance: async (asOfDate: DateTime): Promise<PortfolioPerformance> => {
    const { aggregate, longAggregate, shortAggregate, ...portfolioPerformance } = await httpClient
      .get('/api/portfolio/performance', { searchParams: { valueDate: asOfDate.toISODate() } })
      .json<PortfolioPerformance>();

    const pullTime = DateTime.now();
    return {
      ...portfolioPerformance,
      aggregate: {
        ...aggregate,
        pullTime,
      },
      longAggregate: {
        ...longAggregate,
        pullTime,
      },
      shortAggregate: {
        ...shortAggregate,
        pullTime,
      },
    };
  },

  getEarningsAlerts: async (): Promise<EarningsAlert[]> => {
    const alerts = await httpClient
      .get('/api/notifications', {
        searchParams: { type: 'EARNINGS_ALERT' },
      })
      .json<RawDateTimes<EarningsAlert[]>>();
    return _(alerts)
      .map((alert) => ({
        ...alert,
        beginTime: parseDateTime(alert.beginTime),
        endTime: (alert.endTime && parseDateTime(alert.endTime)) || null,
        parameters: {
          ...alert.parameters,
          earningsDate: parseDateTime(alert.parameters.earningsDate),
          earningsStartTime: parseDateTime(alert.parameters.earningsStartTime),
        },
      }))
      .value();
  },

  getSentimentDirectionAlert: async (): Promise<SentimentAlert[]> => {
    const alerts = await httpClient
      .get('/api/notifications', {
        searchParams: { type: 'BPICS_SENT_CHANGE_ALERT' },
      })
      .json<RawDateTimes<SentimentAlert[]>>();

    return alerts.map(mapAlertWithExpiration) as SentimentAlert[];
  },

  getCrowdingAlert: async (): Promise<CrowdingAlert[]> => {
    const alerts = await httpClient
      .get('/api/notifications', {
        searchParams: { type: 'BPICS_SENT_HEAT_ALERT' },
      })
      .json<RawDateTimes<CrowdingAlert[]>>();

    return alerts.map(mapAlertWithExpiration) as CrowdingAlert[];
  },

  addUserActionOnNotification: async (notificationId: string, input: NotificationActionInput): Promise<void> => {
    await httpClient.post(`/api/notifications/${notificationId}/action`, { json: input });
  },

  addUtmUserTracking: async (input: UtmUserTrackingInput): Promise<void> => {
    await httpClient.post(`/api/analytics/utmUserTracking`, { json: input });
  },
  getTradingAnalytics: async (): Promise<TradingAnalytics> => {
    const tradingAnalytics = await httpClient
      .get('/api/portfolio/analytics/trading')
      .json<RawDateTimes<RawTradingAnalytics>>();

    return tradingAnalyticsFromServerMap(tradingAnalytics);
  },

  getSectors: async (): Promise<Sector[]> => {
    return await httpClient.get('/api/sectors/all').json<Sector[]>();
  },

  getDefaultSector: async (): Promise<Sector> => {
    return await httpClient.get('/api/sectors/default').json<Sector>();
  },

  getCurrentUserSeniority: async (): Promise<AnalystSeniority> => {
    const { seniority } = await httpClient.get('/api/user/details').json<RawAnalystSeniority>();

    return seniority;
  },

  getThemePendingSubmissionDetails: async (analystThemeId: string): Promise<ThemePendingChangeSubmissionDetails> => {
    const submissionDetails: RawDateTimes<ThemePendingChangeSubmissionDetails> = await httpClient
      .get(`/api/themes/submission/${analystThemeId}`)
      .json();

    return {
      ...submissionDetails,
      currentApprovedParentTheme: isNotNullish(submissionDetails.currentApprovedParentTheme)
        ? {
            ...submissionDetails.currentApprovedParentTheme,
            time: parseDateTime(submissionDetails.currentApprovedParentTheme.time),
          }
        : undefined,
      pendingParentTheme: isNotNullish(submissionDetails.pendingParentTheme)
        ? {
            ...submissionDetails.pendingParentTheme,
            time: parseDateTime(submissionDetails.pendingParentTheme.time),
          }
        : undefined,
      currentApprovedAnalystTheme: isNotNullish(submissionDetails.currentApprovedAnalystTheme)
        ? {
            ...submissionDetails.currentApprovedAnalystTheme,
            startDate:
              submissionDetails.currentApprovedAnalystTheme.startDate !== null
                ? parseDateTime(submissionDetails.currentApprovedAnalystTheme.startDate)
                : null,
            time: parseDateTime(submissionDetails.currentApprovedAnalystTheme.time),
            createdTime: parseDateTime(submissionDetails.currentApprovedAnalystTheme.createdTime),
          }
        : undefined,
      pendingAnalystTheme: {
        ...submissionDetails.pendingAnalystTheme,
        startDate:
          submissionDetails.pendingAnalystTheme.startDate !== null
            ? parseDateTime(submissionDetails.pendingAnalystTheme.startDate)
            : null,
        time: parseDateTime(submissionDetails.pendingAnalystTheme.time),
        createdTime: parseDateTime(submissionDetails.pendingAnalystTheme.createdTime),
      },
      currentApprovedThemeCompanies: submissionDetails.currentApprovedThemeCompanies.map((tcs) => ({
        ...tcs,
        time: parseDateTime(tcs.time),
        createdTime: parseDateTime(tcs.createdTime),
      })),
      pendingThemeCompanies: submissionDetails.pendingThemeCompanies.map((tcs) => ({
        ...tcs,
        time: parseDateTime(tcs.time),
        createdTime: parseDateTime(tcs.createdTime),
      })),
    };
  },

  approvePendingThemeChange: async (analystThemeId: string): Promise<void> => {
    await httpClient.post(`/api/themes/submission/${analystThemeId}`, {
      json: 'APPROVED',
    });
  },

  rejectPendingThemeChange: async (analystThemeId: string): Promise<void> => {
    await httpClient.post(`/api/themes/submission/${analystThemeId}`, {
      json: 'REJECTED',
    });
  },

  cancelPendingThemeChange: async (analystThemeId: string): Promise<void> => {
    await httpClient.post(`/api/themes/submission/${analystThemeId}`, {
      json: 'CANCELLED',
    });
  },

  getThemesPerformance: async () => httpClient.get('/api/themes-performance/themes').json<ThemePerformance[]>(),

  getThemeDirectionalPerformance: async (analystThemeId: string) =>
    httpClient
      .get(`/api/themes-performance/directional?analystThemeId=${analystThemeId}`)
      .json<DirectionalThemePerformance>(),

  getThemeCompaniesPerformance: async (analystThemeId: string, bookId: number) =>
    httpClient
      .get(`/api/themes-performance/companies?analystThemeId=${analystThemeId}&bookId=${bookId}`)
      .json<ThemeCompanyPerformance[]>(),
};

export default Api;
